/**
 * Core customer data and CRUD operations
 */

import {
  Customer,
  ProviderCustomerAttributes,
  ProviderCustomerResource,
  ProviderCustomerStatus,
  ProviderCustomersListResponse,
  ProviderCustomerTeamRole,
  ProviderCustomerTeamRoleType,
  ProviderCustomerStatusOption,
} from './types';

export const mockCustomers: Customer[] = [
  {
    id: 'customer-uuid-1',
    firstName: 'John',
    lastName: 'Doe',
    email: 'john@acme.com',
    phone: '+1 (555) 123-4567',
    company: 'Acme Corp',
    status: 'active',
    created_at: '2024-01-15T10:30:00Z',
    updated_at: '2024-12-20T14:45:00Z',
    last_active: '2024-12-20T10:30:00Z',
    
    profile: {
      timezone: 'America/Los_Angeles',
      language: 'en',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
    },
    
    metrics: {
      total_monthly_spend: 1798.80,
      health_score: 92,
      products_used: 4,
      total_operations: 987654,
      credit_balance: 45678,
      avg_daily_operations: 32922,
      credit_usage_rate: 0.85
    },
    
    teams: [], // Will be populated from teams.ts
    individual_subscriptions: [], // Will be populated from subscriptions.ts
    api_keys: [] // Will be populated from api-keys.ts
  },
  
  {
    id: 'customer-uuid-2',
    firstName: 'Sarah',
    lastName: 'Johnson',
    email: 'sarah@techstartup.io',
    company: 'TechStartup Inc.',
    status: 'trial',
    created_at: '2024-11-01T09:00:00Z',
    updated_at: '2024-12-18T11:20:00Z',
    last_active: '2024-12-18T15:45:00Z',
    
    profile: {
      timezone: 'America/New_York',
      language: 'en'
    },
    
    metrics: {
      total_monthly_spend: 0,
      health_score: 78,
      products_used: 2,
      total_operations: 2340,
      credit_balance: 500, // trial credits
      avg_daily_operations: 78,
      credit_usage_rate: 0.25
    },
    
    teams: [],
    individual_subscriptions: [],
    api_keys: []
  },
  
  {
    id: 'customer-uuid-3',
    firstName: 'Michael',
    lastName: 'Chen',
    email: 'michael@globalcorp.com',
    phone: '+1 (555) 987-6543',
    company: 'GlobalCorp Ltd.',
    status: 'active',
    created_at: '2023-08-20T14:15:00Z',
    updated_at: '2024-12-15T16:30:00Z',
    last_active: '2024-12-20T08:00:00Z',
    
    profile: {
      timezone: 'Asia/Singapore',
      language: 'en'
    },
    
    metrics: {
      total_monthly_spend: 2456.70,
      health_score: 95,
      products_used: 6,
      total_operations: 1543210,
      credit_balance: 128900,
      avg_daily_operations: 51440,
      credit_usage_rate: 0.92
    },
    
    teams: [],
    individual_subscriptions: [],
    api_keys: []
  },

  {
    id: 'customer-uuid-4',
    firstName: 'Emily',
    lastName: 'Rodriguez',
    email: 'emily@innovativetech.com',
    phone: '+1 (555) 456-7890',
    company: 'Innovative Tech Solutions',
    status: 'active',
    created_at: '2024-03-10T12:00:00Z',
    updated_at: '2024-12-19T10:15:00Z',
    last_active: '2024-12-19T18:30:00Z',
    
    profile: {
      timezone: 'America/Chicago',
      language: 'en'
    },
    
    metrics: {
      total_monthly_spend: 899.45,
      health_score: 88,
      products_used: 3,
      total_operations: 456789,
      credit_balance: 12500,
      avg_daily_operations: 15226,
      credit_usage_rate: 0.67
    },
    
    teams: [],
    individual_subscriptions: [],
    api_keys: []
  },

  {
    id: 'customer-uuid-5',
    firstName: 'David',
    lastName: 'Kim',
    email: 'david@datadriven.co',
    company: 'DataDriven Analytics',
    status: 'inactive',
    created_at: '2024-02-01T08:30:00Z',
    updated_at: '2024-11-15T16:45:00Z',
    last_active: '2024-11-10T14:22:00Z',
    
    profile: {
      timezone: 'America/Los_Angeles',
      language: 'en'
    },
    
    metrics: {
      total_monthly_spend: 0,
      health_score: 45,
      products_used: 1,
      total_operations: 12340,
      credit_balance: 0,
      avg_daily_operations: 0,
      credit_usage_rate: 0
    },
    
    teams: [],
    individual_subscriptions: [],
    api_keys: []
  }
];

// CRUD Operations
const STATUS_LABELS: Record<ProviderCustomerStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  trial: 'Trial',
  suspended: 'Suspended',
  pending_verification: 'Pending Verification',
};

const STATUS_OPTIONS: ProviderCustomerStatusOption[] = [
  { value: '', label: 'All Status' },
  { value: 'active', label: STATUS_LABELS.active },
  { value: 'inactive', label: STATUS_LABELS.inactive },
  { value: 'trial', label: STATUS_LABELS.trial },
  { value: 'suspended', label: STATUS_LABELS.suspended },
  { value: 'pending_verification', label: STATUS_LABELS.pending_verification },
];

const normalizeStatus = (status: string): ProviderCustomerStatus => {
  switch (status) {
    case 'active':
    case 'inactive':
    case 'trial':
    case 'suspended':
    case 'pending_verification':
      return status;
    default:
      return 'inactive';
  }
};

const buildTeamRoles = (customer: Customer): ProviderCustomerTeamRole[] => {
  const ownerIcon = '👑';
  const adminIcon = '🛠️';
  const memberIcon = '👥';

  const roles: ProviderCustomerTeamRole[] = customer.teams.map(team => {
    const icon = team.role === 'owner' ? ownerIcon : team.role === 'admin' ? adminIcon : memberIcon;
    const label = team.role === 'owner' ? 'Owner' : team.role === 'admin' ? 'Admin' : 'Member';

    return {
      team_id: team.id,
      team_name: team.name,
      role: team.role as ProviderCustomerTeamRoleType,
      icon,
      display: `${icon} ${label}: ${team.name}`,
      status: 'active',
      joined_at: customer.created_at,
    };
  });

  if (roles.length === 0) {
    roles.push({
      team_id: null,
      team_name: null,
      role: 'individual',
      icon: '👤',
      display: '👤 Individual',
      status: normalizeStatus(customer.status),
      joined_at: customer.created_at,
    });
  }

  return roles;
};

const toProviderAttributes = (customer: Customer): ProviderCustomerAttributes => {
  const status = normalizeStatus(customer.status);
  const isTrial = status === 'trial';
  const trialEndsAt = isTrial
    ? new Date(new Date(customer.created_at).getTime() + 14 * 24 * 60 * 60 * 1000).toISOString()
    : null;

  const teamRoles = buildTeamRoles(customer);
  const teamsCount = teamRoles.filter(role => role.role !== 'individual').length;

  return {
    name: `${customer.firstName} ${customer.lastName}`.trim(),
    email: customer.email,
    company_name: customer.company ?? null,
    active: status === 'active',
    status,
    status_label: STATUS_LABELS[status],
    trial_ends_at: trialEndsAt,
    is_trial: isTrial,
    created_at: customer.created_at,
    updated_at: customer.updated_at,
    team_roles: teamRoles,
    is_team_owner: teamRoles.some(role => role.role === 'owner'),
    teams_count: teamsCount,
  };
};

const toJsonApiResource = (customer: Customer): ProviderCustomerResource => ({
  type: 'customers',
  id: customer.id,
  attributes: toProviderAttributes(customer),
});

type CustomerCreatePayload = Partial<Customer> & {
  name?: string;
  role?: 'individual' | 'team_owner';
  team_name?: string;
  teamName?: string;
  company_name?: string;
  send_invite?: boolean;
};

export const customerOperations = {
  list: (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    team_role?: string;
    team_id?: string;
  }): ProviderCustomersListResponse => {
    let filtered = [...mockCustomers];

    if (params?.search) {
      const searchTerm = params.search.toLowerCase();
      filtered = filtered.filter((customer) => {
        const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();
        return (
          fullName.includes(searchTerm) ||
          customer.email.toLowerCase().includes(searchTerm) ||
          customer.company?.toLowerCase().includes(searchTerm)
        );
      });
    }

    if (params?.status) {
      const normalized = normalizeStatus(params.status);
      filtered = filtered.filter((customer) => normalizeStatus(customer.status) === normalized);
    }

    if (params?.team_role) {
      filtered = filtered.filter((customer) => {
        const roles = buildTeamRoles(customer);
        if (params.team_role === 'individual') {
          return roles.some((role) => role.role === 'individual');
        }
        return roles.some((role) => role.role === params.team_role);
      });
    }

    if (params?.team_id) {
      filtered = filtered.filter((customer) => {
        const roles = buildTeamRoles(customer);
        return roles.some((role) => role.team_id === params.team_id);
      });
    }

    const page = params?.page && params.page > 0 ? params.page : 1;
    const perPage = params?.per_page && params.per_page > 0 ? params.per_page : 10;
    const total = filtered.length;
    const lastPage = Math.max(1, Math.ceil(total / perPage));
    const start = (page - 1) * perPage;
    const paginated = filtered.slice(start, start + perPage);

    const data = paginated.map(toJsonApiResource);

    return {
      data,
      meta: {
        current_page: page,
        per_page: perPage,
        total,
        last_page: lastPage,
        from: total === 0 ? 0 : start + 1,
        to: start + data.length,
      },
      status_options: STATUS_OPTIONS,
    };
  },

  getById: (id: string): { data: Customer } => {
    const customer = mockCustomers.find(c => c.id === id);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }
    return { data: customer };
  },

  create: (data: CustomerCreatePayload): { data: Customer } => {
    const now = new Date().toISOString();
    const fullName = (data.name ?? `${data.firstName ?? ''} ${data.lastName ?? ''}`).trim();
    const [derivedFirstNameFromFull, ...restOfName] = fullName.split(' ');
    const derivedLastNameFromFull = restOfName.join(' ').trim();

    const firstName = (data.firstName ?? derivedFirstNameFromFull ?? '').trim();
    const lastName = (data.lastName ?? derivedLastNameFromFull ?? '').trim();
    const company = data.company ?? data.company_name ?? undefined;

    const newCustomer: Customer = {
      id: `customer-uuid-${Date.now()}`,
      firstName,
      lastName,
      email: data.email || '',
      phone: data.phone,
      company,
      status: 'active',
      created_at: now,
      updated_at: now,
      last_active: now,
      profile: {
        timezone: 'UTC',
        language: 'en'
      },
      metrics: {
        total_monthly_spend: 0,
        health_score: 100,
        products_used: 0,
        total_operations: 0,
        credit_balance: 0,
        avg_daily_operations: 0,
        credit_usage_rate: 0
      },
      teams: [],
      individual_subscriptions: [],
      api_keys: []
    };

    if ((data.role ?? 'individual') === 'team_owner') {
      const teamName = (data.team_name ?? data.teamName ?? company ?? `${firstName || 'New'} Team`).trim();
      newCustomer.teams = [
        {
          id: `team-${Date.now()}`,
          name: teamName,
          role: 'owner',
          members: 1,
          total_seats: 5,
          monthly_spend: 0
        }
      ];
    }

    mockCustomers.push(newCustomer);
    return { data: newCustomer };
  },

  update: (id: string, data: Partial<Customer>): { data: Customer } => {
    const index = mockCustomers.findIndex(c => c.id === id);
    if (index === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }
    
    mockCustomers[index] = {
      ...mockCustomers[index],
      ...data,
      updated_at: new Date().toISOString(),
    };
    
    return { data: mockCustomers[index] };
  },

  delete: (id: string): { message: string } => {
    const index = mockCustomers.findIndex(c => c.id === id);
    if (index === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }
    
    mockCustomers.splice(index, 1);
    return { message: 'Customer deleted successfully' };
  }
};
